import React, { useEffect, useRef, useState } from "react";
import "./Recovery.css";
import { account } from "../../appwrite/appwriteConfig";
import LogoDownloadFooter from "../../assets/LogoDownloadFooter.svg";
import backgroundSquareDownload from "../../assets/backgroundSquareDownload.webp";
import passwordIllustration from "../../assets/passwordIllustration.svg";

export default function Recovery() {
    const loginForm = useRef(null);
    const [recoveryState, setRecoveryState] = useState({
        status: "validating_link", // validating_link, form, submitting, success, error
        message: "",
        formErrors: {
            password1: "",
            password2: "",
        },
        dotCount: 1,
    });

    // Handle URL parameters and link validation
    useEffect(() => {
        const validateRecoveryLink = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const secret = urlParams.get("secret");
            const userId = urlParams.get("userId");

            if (!secret || !userId) {
                setRecoveryState((prev) => ({
                    ...prev,
                    status: "error",
                    message:
                        "Invalid recovery link. Please request a new password reset.",
                }));
            } else {
                setRecoveryState((prev) => ({
                    ...prev,
                    status: "form",
                    message: "",
                }));
            }
        };

        validateRecoveryLink();
    }, []);

    // Dot animation effect for loading states
    useEffect(() => {
        let interval;
        if (
            recoveryState.status === "submitting" ||
            recoveryState.status === "success"
        ) {
            interval = setInterval(() => {
                setRecoveryState((prev) => ({
                    ...prev,
                    dotCount: (prev.dotCount % 3) + 1,
                }));
            }, 400);
        }
        return () => clearInterval(interval);
    }, [recoveryState.status]);

    // Handle form validation
    const validateForm = () => {
        const password1 = loginForm.current.password1.value;
        const password2 = loginForm.current.password2.value;
        const errors = {};

        if (!password1) errors.password1 = "Password is required";
        else if (password1.length < 8)
            errors.password1 = "Password must be at least 8 characters";

        if (!password2) errors.password2 = "Please confirm your password";
        else if (password1 !== password2)
            errors.password2 = "Passwords do not match";

        return errors;
    };

    // Handle password reset submission
    const handlePasswordReset = async (e) => {
        e.preventDefault();

        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setRecoveryState((prev) => ({
                ...prev,
                formErrors,
            }));
            return;
        }

        try {
            setRecoveryState((prev) => ({
                ...prev,
                status: "submitting",
                formErrors: { password1: "", password2: "" },
            }));

            const password1 = loginForm.current.password1.value;
            const urlParams = new URLSearchParams(window.location.search);

            await account.updateRecovery(
                urlParams.get("userId"),
                urlParams.get("secret"),
                password1
            );

            setRecoveryState((prev) => ({
                ...prev,
                status: "success",
                message: "Password reset successful! Redirecting...",
            }));

            // Redirect to login after delay
            setTimeout(() => {
                window.location.href = "https://www.chess.com";
            }, 3000);
        } catch (err) {
            setRecoveryState((prev) => ({
                ...prev,
                status: "error",
                message:
                    err.message || "Password reset failed. Please try again.",
            }));
        }
    };

    // Render loading dots animation
    const renderLoadingDots = () => (
        <span>{".".repeat(recoveryState.dotCount)}</span>
    );

    // Render form error messages
    const renderFormError = (field) =>
        recoveryState.formErrors[field] && (
            <div className="input-error">{recoveryState.formErrors[field]}</div>
        );

    // Main component render
    return (
        <>
            <title>Account Recovery | Oakmate</title>
            <meta
                name="description"
                content="Recover access to your Oakmate account."
            />
            <div className="recoveryPage">
                <BackgroundElements />

                {recoveryState.status === "error" ? (
                    <RecoveryError message={recoveryState.message} />
                ) : recoveryState.status === "success" ? (
                    <RecoverySuccess
                        message={recoveryState.message}
                        dotCount={renderLoadingDots()}
                    />
                ) : recoveryState.status === "submitting" ? (
                    <RecoverySubmitting
                        message="Processing your request"
                        dotCount={renderLoadingDots()}
                    />
                ) : recoveryState.status === "form" ? (
                    <RecoveryForm
                        formRef={loginForm}
                        onSubmit={handlePasswordReset}
                        renderFormError={renderFormError}
                    />
                ) : (
                    <RecoveryValidating dotCount={renderLoadingDots()} />
                )}

                <Copyright />
            </div>
        </>
    );
}

// Sub-components for better organization
const BackgroundElements = () => (
    <>
        <img
            src={backgroundSquareDownload}
            alt="Background"
            className="backgroundSquareDownload"
        />
        <div className="oakmatebackgroundlogo-wrapper">
            <img
                src={LogoDownloadFooter}
                alt="Logo background"
                className="LogoDownloadFooter"
            />
            <div className="maskdownloadfooter" />
        </div>
    </>
);

const RecoveryError = ({ message }) => (
    <div className="error-state">
        <div className="text-title">Recovery Failed</div>
        <div className="text-sub error">{message}</div>
    </div>
);

const RecoverySuccess = ({ message, dotCount }) => (
    <div className="success-state">
        <div className="text-title">Success!</div>
        <div className="text-sub">
            {message}
            {dotCount}
        </div>
    </div>
);

const RecoverySubmitting = ({ message, dotCount }) => (
    <div className="submitting-state">
        <div className="text-title">Processing</div>
        <div className="text-sub">
            {message}
            {dotCount}
        </div>
    </div>
);

const RecoveryValidating = ({ dotCount }) => (
    <div className="validating-state">
        <div className="text-title">Validating Link</div>
        <div className="text-sub">
            Please wait
            {dotCount}
        </div>
    </div>
);

const RecoveryForm = ({ formRef, onSubmit, renderFormError }) => (
    <div className="download-container">
        <div className="main-wrapper">
            <div className="passwordIllu-container">
                <img
                    src={passwordIllustration}
                    alt="password Illustration"
                    className="passwordIllustration"
                />
            </div>
            <div className="text-wrapper">
                <div className="text-title">Set new password</div>
                <div className="text-sub">
                    Must be at least 8 characters long
                </div>
            </div>
            <form className="auth-form" onSubmit={onSubmit} ref={formRef}>
                <div className="auth-form-main">
                    <FormField
                        name="password1"
                        type="password"
                        label="Password"
                        error={renderFormError("password1")}
                    />
                    <FormField
                        name="password2"
                        type="password"
                        label="Confirm Password"
                        error={renderFormError("password2")}
                    />
                </div>
                <button type="submit" className="recovery-btn">
                    <div className="recovery-btn-text">Reset Password</div>
                </button>
            </form>
        </div>
    </div>
);

const FormField = ({ name, type, label, error }) => (
    <label className="form-field glassy-field">
        <input
            type={type}
            placeholder=" "
            name={name}
            className="form-input"
            autoComplete="off"
        />
        <span className="form-label">{label}</span>
        {error}
    </label>
);

const Copyright = () => (
    <div className="copyright">
        Copyright © 2025 Oakmate. All rights reserved
    </div>
);
